import { supabase } from '../config/supabase';
import { fetchSystemSettings } from '../utils/tripActions';
import { planDailySchedule, planToPublishPayload } from '../scheduling/planDailySchedule';
import { jobLegsToScheduleInputs } from '../scheduling/jobLegConverters';
import { groupFixedLegsByVehicle } from '../scheduling/fixedTripBlocks';
import {
  findIntraPlanDriverConflicts,
  planNewAssignmentWindows,
  planNewDriverAssignmentWindows,
} from '../scheduling/publishWindows';
import {
  formatPublishScheduleError,
  jobsMissingOrgForHqPublish,
} from '../scheduling/publishOrgGuard';
import {
  parseSchedulingSettings,
  schedulingSettingsToTravelOptions,
} from '../scheduling/travelTime';
import {
  collectDeadheadOdPairs,
  prefetchDirectionsTravelMinutes,
} from '../scheduling/prefetchDirectionsTravel';
import { checkDriverScheduleConflict, checkVehicleScheduleConflict, formatScheduleConflictMessage } from './fleetSchedule';
import { isDriverBlockedByFullDayEscort } from './fullDayEscort';
import { refreshTripsRouteEtaForDate } from './tripRouteEta';
import type {
  DailySchedulePlan,
  ScheduleJobInput,
  ScheduleVehicleInput,
  TravelTimeOptions,
} from '../scheduling/types';
import type { Job, JobLeg, Trip } from '../types/database';

/** Confirmed default for M1 chain / publish buffer (CONF-20260714). */
export const DEFAULT_CHAIN_BUFFER_MINUTES = 15;

export interface DirectionsPrefetchSummary {
  requestedPairs: number;
  resolvedPairs: number;
  cacheHits: number;
  fetchedPairs: number;
  hadError: boolean;
  rateLimited: boolean;
}

export interface DailyScheduleContext {
  jobs: ScheduleJobInput[];
  vehicles: ScheduleVehicleInput[];
  scheduleDate: string;
  fixedLegsByVehicle: Record<string, import('../scheduling/types').ScheduleLegInput[]>;
  travelOptions: TravelTimeOptions;
  jobTitles: Record<string, string>;
  /** Present when travel_time_mode=directions (W2). */
  directionsPrefetch?: DirectionsPrefetchSummary;
}

type JobWithLegs = Job & { job_legs: JobLeg[] };

async function fetchPublishedTripsForDate(scheduleDate: string): Promise<Trip[]> {
  const { data, error } = await supabase
    .from('trips')
    .select(
      'id, date, pickup_time, end_time, pickup_address, dropoff_address, pickup_geopoint, dropoff_geopoint, passenger_count, status, vehicle_id, job_id, created_at, updated_at'
    )
    .eq('date', scheduleDate)
    .in('status', ['scheduled', 'dispatched', 'in_progress'])
    .not('vehicle_id', 'is', null);

  if (error) throw new Error(error.message);
  return (data ?? []) as Trip[];
}

async function resolveScheduleBufferMinutes(): Promise<number> {
  const { data } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'schedule_buffer_minutes')
    .maybeSingle();

  const value = data?.value as { minutes?: unknown } | null | undefined;
  if (value && typeof value.minutes === 'number' && value.minutes >= 0) {
    return value.minutes;
  }
  return DEFAULT_CHAIN_BUFFER_MINUTES;
}

export async function fetchDailyScheduleContext(
  scheduleDate: string
): Promise<DailyScheduleContext> {
  const [jobsRes, vehiclesRes, settings, trips, bufferMinutes] = await Promise.all([
    supabase
      .from('jobs')
      .select('*, job_legs(*)')
      .eq('schedule_date', scheduleDate)
      .in('status', ['unscheduled', 'planning'])
      .neq('assist_status', 'pending_platform')
      .order('created_at'),
    supabase
      .from('vehicles')
      .select('id, fleet_number, capacity, fleet_operator_id')
      .eq('status', 'active')
      .order('fleet_number'),
    fetchSystemSettings(),
    fetchPublishedTripsForDate(scheduleDate),
    resolveScheduleBufferMinutes(),
  ]);

  if (jobsRes.error) throw new Error(jobsRes.error.message);
  if (vehiclesRes.error) throw new Error(vehiclesRes.error.message);

  const baseTravel = schedulingSettingsToTravelOptions(parseSchedulingSettings(settings));
  let travelOptions: TravelTimeOptions = {
    ...baseTravel,
    chainBufferMinutes: bufferMinutes,
  };

  const jobs: ScheduleJobInput[] = [];
  for (const row of (jobsRes.data ?? []) as JobWithLegs[]) {
    // Pending platform assist is handled by HQ — fleet does not schedule these.
    if (row.assist_status === 'pending_platform') continue;
    const legs = jobLegsToScheduleInputs(row.job_legs ?? [], row.id, row.title);
    if (legs.length === 0) continue;
    jobs.push({
      id: row.id,
      title: row.title,
      jobType: row.job_type,
      passengerCount: row.passenger_count,
      orgId: row.org_id ?? null,
      fleetOperatorId: row.fleet_operator_id ?? null,
      driverFullDayEscort: Boolean(row.driver_full_day_escort),
      legs,
    });
  }

  const vehicles: ScheduleVehicleInput[] = (vehiclesRes.data ?? []).map((v) => ({
    id: v.id,
    fleetNumber: v.fleet_number,
    capacity: v.capacity,
    fleetOperatorId: v.fleet_operator_id ?? null,
  }));

  const fixedLegsByVehicle = groupFixedLegsByVehicle(trips);

  const jobTitles: Record<string, string> = {};
  for (const j of jobs) jobTitles[j.id] = j.title;
  const publishedJobIds = [
    ...new Set(trips.map((t) => t.job_id).filter((id): id is string => Boolean(id))),
  ].filter((id) => !jobTitles[id]);
  if (publishedJobIds.length > 0) {
    const { data: titled, error: titleErr } = await supabase
      .from('jobs')
      .select('id, title')
      .in('id', publishedJobIds);
    if (titleErr) throw new Error(titleErr.message);
    for (const row of titled ?? []) {
      jobTitles[row.id as string] = row.title as string;
    }
  }

  let directionsPrefetch: DirectionsPrefetchSummary | undefined;
  if (travelOptions.travelTimeMode === 'directions') {
    const pairs = collectDeadheadOdPairs(jobs, fixedLegsByVehicle);
    const pref = await prefetchDirectionsTravelMinutes(pairs);
    travelOptions = {
      ...travelOptions,
      directionsMinutes: pref.directionsMinutes,
    };
    directionsPrefetch = {
      requestedPairs: pref.requestedPairs,
      resolvedPairs: pref.resolvedPairs,
      cacheHits: pref.cacheHits,
      fetchedPairs: pref.fetchedPairs,
      hadError: pref.hadError,
      rateLimited: pref.rateLimited,
    };
  }

  return {
    jobs,
    vehicles,
    scheduleDate,
    fixedLegsByVehicle,
    travelOptions,
    jobTitles,
    directionsPrefetch,
  };
}

export async function planDailyScheduleForDate(
  scheduleDate: string,
  options?: { pinnedAssignments?: Record<string, string> }
): Promise<DailySchedulePlan> {
  const ctx = await fetchDailyScheduleContext(scheduleDate);
  const plan = planDailySchedule({
    jobs: ctx.jobs,
    vehicles: ctx.vehicles,
    travelOptions: ctx.travelOptions,
    fixedLegsByVehicle: ctx.fixedLegsByVehicle,
    jobTitles: ctx.jobTitles,
    pinnedAssignments: options?.pinnedAssignments,
  });
  if (!ctx.directionsPrefetch) return plan;
  return { ...plan, directionsPrefetch: ctx.directionsPrefetch };
}

export interface PublishScheduleResult {
  trips_created: number;
  jobs_scheduled: number;
}

/** Reject publish when new assignment windows overlap existing trips (incl. buffer). */
export async function assertPlanNoVehicleConflicts(
  scheduleDate: string,
  plan: DailySchedulePlan,
  bufferMinutes = DEFAULT_CHAIN_BUFFER_MINUTES
): Promise<void> {
  const windows = planNewAssignmentWindows(plan);
  const buffer = plan.travelOptions?.chainBufferMinutes ?? bufferMinutes;

  for (const window of windows) {
    const rows = await checkVehicleScheduleConflict({
      vehicleId: window.vehicleId,
      date: scheduleDate,
      pickupTime: window.pickupTime,
      endTime: window.endTime,
      bufferMinutes: buffer,
    });
    if (rows.length > 0) {
      throw new Error(
        `Vehicle conflict for job ${window.jobId.slice(0, 8)}: ${formatScheduleConflictMessage(rows)}`
      );
    }
  }
}

function jobLabel(plan: DailySchedulePlan, jobId: string): string {
  return plan.jobTitles?.[jobId] ?? `Job ${jobId.slice(0, 8)}`;
}

function hhmm(time: string): string {
  return time.slice(0, 5);
}

/**
 * Reject publish when the same driver appears on overlapping new legs
 * (across vehicles or within one chain) or vs existing trips — matches
 * publish_schedule_plan / check_driver_schedule_conflict buffer rules.
 */
export async function assertPlanNoDriverConflicts(
  scheduleDate: string,
  plan: DailySchedulePlan,
  bufferMinutes = DEFAULT_CHAIN_BUFFER_MINUTES
): Promise<void> {
  const buffer = plan.travelOptions?.chainBufferMinutes ?? bufferMinutes;
  const intra = findIntraPlanDriverConflicts(plan, buffer);
  if (intra.length > 0) {
    const { a, b } = intra[0];
    throw new Error(
      `Driver conflict: same driver on overlapping jobs ` +
        `${jobLabel(plan, a.jobId)} (${hhmm(a.pickupTime)}–${hhmm(a.endTime)}) and ` +
        `${jobLabel(plan, b.jobId)} (${hhmm(b.pickupTime)}–${hhmm(b.endTime)}) ` +
        `including ${buffer}m buffer. Assign a different driver on one vehicle, or leave at least ${buffer} minutes between jobs.`
    );
  }

  const windows = planNewDriverAssignmentWindows(plan);
  for (const window of windows) {
    const rows = await checkDriverScheduleConflict({
      driverId: window.driverId,
      date: scheduleDate,
      pickupTime: window.pickupTime,
      endTime: window.endTime,
      bufferMinutes: buffer,
    });
    if (rows.length > 0) {
      throw new Error(
        `Driver conflict for ${jobLabel(plan, window.jobId)}: ${formatScheduleConflictMessage(rows)}`
      );
    }
  }
}

export async function assertPlanNoFullDayEscortConflicts(
  scheduleDate: string,
  plan: DailySchedulePlan
): Promise<void> {
  const jobs = plan.jobs ?? [];
  const escortJobIds = new Set(
    jobs.filter((j) => j.driverFullDayEscort).map((j) => j.id)
  );

  for (const v of plan.vehicles) {
    if (!v.driverId) continue;
    const jobIds = [
      ...new Set(
        (v.legs ?? [])
          .map((l) => l.jobId)
          .filter((id): id is string => Boolean(id))
      ),
    ];
    if (jobIds.length === 0) continue;

    const assigningEscort = jobIds.some((id) => escortJobIds.has(id));
    // Same-vehicle multi-job with escort + other jobs in plan = reject
    if (assigningEscort && jobIds.length > 1) {
      throw new Error(
        `FULL_DAY_ESCORT: driver on ${v.fleetNumber ?? v.vehicleId} cannot combine full-day escort with other jobs`
      );
    }
    // Escort vehicle must be the only assignment for that driver that day in the plan
    if (assigningEscort) {
      const otherVehicles = plan.vehicles.filter(
        (o) => o.vehicleId !== v.vehicleId && o.driverId === v.driverId
      );
      if (otherVehicles.length > 0) {
        throw new Error(
          `FULL_DAY_ESCORT: same driver assigned to multiple vehicles with full-day escort rules`
        );
      }
    }

    const blocked = await isDriverBlockedByFullDayEscort({
      driverId: v.driverId,
      date: scheduleDate,
      assigningEscort,
    });
    if (blocked) {
      throw new Error(
        `FULL_DAY_ESCORT: driver unavailable on ${scheduleDate} for ${jobLabel(plan, jobIds[0])}`
      );
    }
  }
}

export async function publishSchedulePlan(
  scheduleDate: string,
  plan: DailySchedulePlan
): Promise<PublishScheduleResult> {
  const missingOrg = jobsMissingOrgForHqPublish(plan);
  if (missingOrg.length > 0) {
    throw new Error(formatPublishScheduleError('JOB_ORG_REQUIRED', missingOrg));
  }

  await assertPlanNoVehicleConflicts(scheduleDate, plan);
  await assertPlanNoDriverConflicts(scheduleDate, plan);
  await assertPlanNoFullDayEscortConflicts(scheduleDate, plan);
  const payload = planToPublishPayload(plan);
  const { data, error } = await supabase.rpc('publish_schedule_plan', {
    p_date: scheduleDate,
    p_plan: payload,
  });

  if (error) {
    throw new Error(formatPublishScheduleError(error.message, missingOrg));
  }
  const result = data as PublishScheduleResult;

  // CONF-20260719-trip-route-eta: refresh passenger-leg route ETA after publish
  try {
    await refreshTripsRouteEtaForDate(scheduleDate);
  } catch (etaErr) {
    console.warn('refreshTripsRouteEtaForDate failed after publish:', etaErr);
  }

  return {
    trips_created: result.trips_created ?? 0,
    jobs_scheduled: result.jobs_scheduled ?? 0,
  };
}
